import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Alert,
  Snackbar,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Print as PrintIcon,
  NetworkWifi as NetworkIcon,
  Storage as StorageIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Terminal as TerminalIcon,
  Security as SecurityIcon,
  Print as PrintIconOutlined,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PrintServiceClient = ({ language = 'en' }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [copied, setCopied] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [copiedCommand, setCopiedCommand] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // API Key states
  const [apiKeyData, setApiKeyData] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [manualApiKey, setManualApiKey] = useState('');
  const [manualApiSecret, setManualApiSecret] = useState('');

  // Language translations
  const translations = {
    ar: {
      title: "خدمة طباعة POS",
      version: "الإصدار 1.0.0",
      description: "قم بتثبيت وتكوين عميل خدمة الطباعة لبدء طباعة الإيصالات وطلبات المطبخ",
      downloadBtn: "تحميل عميل الطباعة",
      downloadStarted: "✓ بدأ التحميل!",
      systemRequirements: "متطلبات النظام",
      installationGuide: "دليل التثبيت",
      installationSubtitle: "اتبع هذه الخطوات لإعداد عميل خدمة الطباعة",
      quickCommands: "الأوامر السريعة",
      troubleshooting: "استكشاف الأخطاء وإصلاحها",
      needHelp: "تحتاج مساعدة؟",
      supportText: "اتصل بفريق الدعم للحصول على مساعدة في التثبيت أو التكوين",
      copy: "نسخ",
      copied: "تم النسخ!",
      startServer: "تشغيل الخادم يدويًا",
      directStart: "تشغيل مباشر باستخدام Node.js",
      devMode: "وضع التطوير",
      uninstall: "إلغاء التثبيت",
      footer: "جميع الحقوق محفوظة © 2026 نظام نقاط البيع QR Table",
      lastUpdated: "آخر تحديث: ديسمبر 2024",
      step1Title: "تحميل العميل",
      step1Desc: "قم بتنزيل حزمة تثبيت خدمة طباعة POS",
      step1Command: "تحميل POS_Print_Client.zip",
      step1Details: "انقر على زر التحميل أدناه للحصول على أحدث إصدار من عميل خدمة الطباعة. تتضمن الحزمة جميع الملفات اللازمة لخدمة الطباعة.",
      step2Title: "فك الضغط",
      step2Desc: "استخرج ملف zip المحمل إلى الموقع المطلوب",
      step2Command: "زر الفأرة الأيمن ← استخراج الكل",
      step2Details: "قم باستخراج ملف ZIP إلى مجلد على جهاز الكمبيوتر الخاص بك. نوصي باستخراج إلى 'C:\\POS_Print_Service' أو سطح المكتب لسهولة الوصول.",
      step3Title: "تشغيل المثبت",
      step3Desc: "قم بتشغيل install.bat كمسؤول",
      step3Command: "زر الفأرة الأيمن على install.bat ← تشغيل كمسؤول",
      step3Details: "انتقل إلى المجلد المستخرج، انقر بزر الماوس الأيمن على 'install.bat' واختر 'تشغيل كمسؤول'. سيؤدي هذا إلى تثبيت Node.js (إذا لزم الأمر) وجميع التبعيات المطلوبة.",
      step4Title: "إدخال مفتاح API",
      step4Desc: "أدخل مفتاح API الخاص بك من لوحة تحكم المسؤول",
      step4Command: apiKeyData?.apiKey,
      step4Details: "أثناء التثبيت، سيُطلب منك إدخال مفتاح API الخاص بك. يمكنك العثور على هذا المفتاح/إنشاؤه في لوحة تحكم مسؤول QR Table ضمن الإعدادات ← مفاتيح API.",
      step5Title: "بدء الخادم",
      step5Desc: "قم بتشغيل الخادم باستخدام الاختصار الذي تم إنشاؤه",
      step5Command: "نقرتين متتاليتين على 'POS Backend Server' على سطح المكتب آو تشغيل 'run-server.bat'",
      step5Details: "بعد التثبيت الناجح، انقر نقرًا مزدوجًا على اختصار 'POS Backend Server' على سطح المكتب آو تشغيل 'run-server.bat'. سيبدأ الخادم ويعرض السجلات في نافذة الأوامر. أبقِه قيد التشغيل لكي تعمل خدمة الطباعة!",
      reqWindows: "ويندوز 10/11",
      reqWindowsDesc: "نظام تشغيل 64 بت",
      reqPrinter: "دعم الطابعة",
      reqPrinterDesc: "طابعات متوافقة مع ESC/POS",
      reqNetwork: "الوصول إلى الشبكة",
      reqNetworkDesc: "اتصال LAN أو USB",
      reqStorage: "مساحة التخزين",
      reqStorageDesc: "500 ميغابايت مساحة حرة",
      troubleServer: "الخادم لا يعمل؟",
      troubleServerDesc: "تأكد من تشغيل install.bat كمسؤول ومن تثبيت Node.js بشكل صحيح.",
      troubleApiKey: "مفتاح API غير صالح؟",
      troubleApiKeyDesc: "قم بإنشاء مفتاح API جديد من لوحة تحكم المسؤول وقم بتشغيل اختصار 'تحديث مفتاح API'.",
      troublePrinter: "الطابعة لا تعمل؟",
      troublePrinterDesc: "تحقق من توصيلات الطابعة وتحقق من عناوين IP في ملف تكوين .env.",
      troublePort: "تعارض المنافذ؟",
      troublePortDesc: "قم بتغيير PORT في ملف .env إذا كان المنفذ 5006 قيد الاستخدام بالفعل.",
      supportEmail: "support@qr-table.com",
      documentation: "الوثائق",
      faq: "الأسئلة الشائعة",
      viewApiKey: "عرض مفتاح API",
      apiKeyInfo: "معلومات مفتاح API",
      apiKey: "مفتاح API",
      apiSecret: "السر",
      status: "الحالة",
      lastUsed: "آخر استخدام",
      expiresAt: "تاريخ الانتهاء",
      neverExpires: "لا ينتهي",
      notUsedYet: "لم يستخدم بعد",
      active: "نشط",
      inactive: "غير نشط",
      revoked: "ملغي",
      copyKey: "نسخ المفتاح",
      copySecret: "نسخ السر",
      close: "إغلاق",
      yourApiKey: "مفتاح API الخاص بك",
      enterApiKey: "أدخل مفتاح API",
      enterApiSecret: "أدخل السر",
      configureApiKey: "تكوين مفتاح API",
      saveConfiguration: "حفظ الإعدادات"
    },
    en: {
      title: "POS Print Service",
      version: "v1.0.0",
      description: "Install and configure your POS print service client to start printing receipts and kitchen orders",
      downloadBtn: "Download Print Client",
      downloadStarted: "✓ Download started!",
      systemRequirements: "System Requirements",
      installationGuide: "Installation Guide",
      installationSubtitle: "Follow these steps to set up your print service client",
      quickCommands: "Quick Commands",
      troubleshooting: "Troubleshooting",
      needHelp: "Need Help?",
      supportText: "Contact our support team for assistance with installation or configuration",
      copy: "Copy",
      copied: "Copied!",
      startServer: "Start the server manually",
      directStart: "Direct Node.js start",
      devMode: "Development mode",
      uninstall: "Uninstall service",
      footer: "© 2026 QR Table POS System. All rights reserved.",
      lastUpdated: "Last updated: December 2024",
      step1Title: "Download Client",
      step1Desc: "Download the POS Print Service Client installer package",
      step1Command: "https://qr-table.com/POS_Print_Client.zip",
      step1Details: "Click the download button below to get the latest version of the POS Print Service Client. The package includes all necessary files for the print service.",
      step2Title: "Unzip Files",
      step2Desc: "Extract the downloaded zip file to your desired location",
      step2Command: "Right-click → Extract All",
      step2Details: "Extract the ZIP file to a folder on your computer. We recommend extracting to 'C:\\POS_Print_Service' or your desktop for easy access.",
      step3Title: "Run Installer",
      step3Desc: "Run install.bat as Administrator",
      step3Command: "Right-click install.bat → Run as Administrator",
      step3Details: "Navigate to the extracted folder, right-click on 'install.bat' and select 'Run as Administrator'. This will install Node.js (if needed) and all required dependencies.",
      step4Title: "Enter API Key",
      step4Desc: "Enter your API key from the admin dashboard",
      step4Command: apiKeyData?.apiKey,
      step4Details: "During installation, you'll be prompted to enter your API key. You can find/generate this key in your QR Table Admin Dashboard under Print Settings → POS Print Service → View API Keys.",
      step5Title: "Start Server",
      step5Desc: "Run the server using the generated shortcut",
      step5Command: "Double-click 'POS Backend Server' on Desktop or run 'run-server.bat'",
      step5Details: "After successful installation, double-click the 'POS Backend Server' shortcut on your desktop or run 'run-server.bat'. The server will start and show logs in the command window. Keep it running for print service to work!",
      reqWindows: "Windows 10/11",
      reqWindowsDesc: "64-bit operating system",
      reqPrinter: "Printer Support",
      reqPrinterDesc: "ESC/POS compatible printers",
      reqNetwork: "Network Access",
      reqNetworkDesc: "LAN or USB connection",
      reqStorage: "Storage",
      reqStorageDesc: "500MB free space",
      troubleServer: "Server won't start?",
      troubleServerDesc: "Make sure you ran install.bat as Administrator and Node.js is properly installed.",
      troubleApiKey: "Invalid API Key?",
      troubleApiKeyDesc: "Generate a new API key from the admin dashboard and run the 'Update API Key' shortcut.",
      troublePrinter: "Printer not working?",
      troublePrinterDesc: "Check printer connections and verify IP addresses in the .env file configuration.",
      troublePort: "Port conflicts?",
      troublePortDesc: "Change the PORT in .env file if 5006 is already in use.",
      supportEmail: "support@qr-table.com",
      documentation: "Documentation",
      faq: "FAQ",
      viewApiKey: "View API Key",
      apiKeyInfo: "API Key Information",
      apiKey: "API Key",
      apiSecret: "API Secret",
      status: "Status",
      lastUsed: "Last Used",
      expiresAt: "Expires At",
      neverExpires: "Never expires",
      notUsedYet: "Not used yet",
      active: "Active",
      inactive: "Inactive",
      revoked: "Revoked",
      copyKey: "Copy API Key",
      copySecret: "Copy Secret",
      close: "Close",
      yourApiKey: "Your API Key",
      enterApiKey: "Enter API Key",
      enterApiSecret: "Enter API Secret",
      configureApiKey: "Configure API Key",
      saveConfiguration: "Save Configuration"
    }
  };

  const t = translations[language] || translations.en;
  const isRtl = language === 'ar';

  // Fetch API key for the current user's restaurant
  useEffect(() => {
    if (user?.restaurant_id) {
      fetchApiKey();
    }
  }, [user]);

  const fetchApiKey = async () => {
    setApiKeyLoading(true);
    try {
      const response = await api.get(`/pos-api/restaurants/${user.restaurant_id}/api-key`);
      setApiKeyData(response.data.data);
      setManualApiKey(response.data.data.apiKey);
      setManualApiSecret(response.data.data.apiSecret);
    } catch (error) {
      if (error.response?.status === 404) {
        setApiKeyData(null);
      } else {
        console.error('Error fetching API key:', error);
        setSnackbar({
          open: true,
          message: language === 'ar' ? 'فشل في تحميل مفتاح API' : 'Failed to load API key',
          severity: 'error'
        });
      }
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: type === 'key' ? (language === 'ar' ? 'تم نسخ مفتاح API' : 'API Key copied') : (language === 'ar' ? 'تم نسخ السر' : 'API Secret copied'),
      severity: 'success'
    });
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'active':
        return <Chip label={t.active} color="success" size="small" icon={<CheckCircleIcon />} />;
      case 'inactive':
        return <Chip label={t.inactive} color="warning" size="small" />;
      case 'revoked':
        return <Chip label={t.revoked} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const steps = [
    {
      id: 1,
      title: t.step1Title,
      description: t.step1Desc,
      command: t.step1Command,
      details: t.step1Details,
      icon: <DownloadIcon />
    },
    {
      id: 2,
      title: t.step2Title,
      description: t.step2Desc,
      command: t.step2Command,
      details: t.step2Details,
      icon: <ComputerIcon />
    },
    {
      id: 3,
      title: t.step3Title,
      description: t.step3Desc,
      command: t.step3Command,
      details: t.step3Details,
      icon: <TerminalIcon />
    },
    {
      id: 4,
      title: t.step4Title,
      description: t.step4Desc,
      command: t.step4Command,
      details: t.step4Details,
      icon: <SecurityIcon />
    },
    {
      id: 5,
      title: t.step5Title,
      description: t.step5Desc,
      command: t.step5Command,
      details: t.step5Details,
      icon: <PrintIconOutlined />
    }
  ];

 const handleDownload = async () => {
  const fileUrl = 'https://qr-table.com/POS_Print_Client.zip';
  
  try {
    // Check if file exists before attempting download
    const response = await fetch(fileUrl, { method: 'HEAD' });
    
    if (!response.ok) {
      throw new Error('File not found');
    }
    
    // File exists, proceed with download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'POS_Print_Client.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSnackbar({
      open: true,
      message: t.downloadStarted,
      severity: 'success'
    });
  } catch (error) {
    console.error('Download failed:', error);
    setSnackbar({
      open: true,
      message: language === 'ar' ? 'فشل التحميل. الملف غير موجود' : 'Download failed. File not found',
      severity: 'error'
    });
  }
};

  const copyCommand = (command) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(command);
    setSnackbar({
      open: true,
      message: t.copied,
      severity: 'success'
    });
    setTimeout(() => setCopiedCommand(''), 2000);
  };

  const requirements = [
    { icon: <ComputerIcon />, title: t.reqWindows, desc: t.reqWindowsDesc },
    { icon: <PrintIcon />, title: t.reqPrinter, desc: t.reqPrinterDesc },
    { icon: <NetworkIcon />, title: t.reqNetwork, desc: t.reqNetworkDesc },
    { icon: <StorageIcon />, title: t.reqStorage, desc: t.reqStorageDesc }
  ];

  const commands = [
    { command: 'npm start', desc: t.startServer },
    { command: 'node server.js', desc: t.directStart },
    { command: 'npm run dev', desc: t.devMode },
    { command: 'uninstall.bat', desc: t.uninstall }
  ];

  const troubles = [
    { icon: <HelpIcon />, title: t.troubleServer, desc: t.troubleServerDesc },
    { icon: <SecurityIcon />, title: t.troubleApiKey, desc: t.troubleApiKeyDesc },
    { icon: <PrintIcon />, title: t.troublePrinter, desc: t.troublePrinterDesc },
    { icon: <NetworkIcon />, title: t.troublePort, desc: t.troublePortDesc }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#0D0D0D',
      color: '#FFFFFF',
      direction: isRtl ? 'rtl' : 'ltr'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        bgcolor: 'linear-gradient(135deg, #1A1A1A 0%, #0D0D0D 100%)',
        borderBottom: '2px solid #FFD700',
        py: { xs: 4, md: 6 },
        mb: 4
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  color: '#FFD700'
                }}
              >
                {t.title}
              </Typography>
              <Chip 
                label={t.version}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000000',
                  fontWeight: 700,
                  height: '28px'
                }}
              />
            </Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#B3B3B3',
                mb: 3,
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              {t.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000000',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    bgcolor: '#FFE55C',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {t.downloadBtn}
              </Button>
              
              {user?.restaurant_id && (
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<VpnKeyIcon />}
                  onClick={() => setApiKeyDialogOpen(true)}
                  sx={{
                    borderColor: '#FFD700',
                    color: '#FFD700',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    '&:hover': {
                      borderColor: '#FFE55C',
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {t.viewApiKey}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mb: 6 }}>
        {/* System Requirements */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 700,
              mb: 3,
              textAlign: 'center'
            }}
          >
            {t.systemRequirements}
          </Typography>
          <Grid container spacing={3}>
            {requirements.map((req, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{
                  bgcolor: '#1A1A1A',
                  border: '1px solid rgba(255, 215, 0, 0.2)',
                  borderRadius: '16px',
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#FFD700',
                    transform: 'translateY(-4px)'
                  }
                }}>
                  <CardContent>
                    <Box sx={{ color: '#FFD700', fontSize: 40, mb: 2 }}>
                      {req.icon}
                    </Box>
                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                      {req.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {req.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Installation Steps */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 700,
              mb: 1,
              textAlign: 'center'
            }}
          >
            {t.installationGuide}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#B3B3B3',
              mb: 4,
              textAlign: 'center'
            }}
          >
            {t.installationSubtitle}
          </Typography>

<Stepper activeStep={activeStep} orientation="vertical" sx={{ gap: 2 }}>
  {steps.map((step, index) => (
    <Step key={step.id}>
      <StepLabel 
        onClick={() => setActiveStep(index)}
        sx={{ cursor: 'pointer' }}
        StepIconProps={{
          sx: {
            '& .MuiStepIcon-root': {
              color: activeStep >= index ? '#FFD700' : '#2D2D2D',
              '& .MuiStepIcon-text': {
                fill: activeStep >= index ? '#000000' : '#FFFFFF'
              }
            }
          }
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            color: activeStep >= index ? '#FFD700' : '#FFFFFF', 
            fontWeight: 700,
            '&:hover': {
              color: '#FFD700'
            }
          }}
        >
          {step.title}
        </Typography>
      </StepLabel>
      <StepContent>
        <Paper sx={{
          p: 3,
          bgcolor: '#1A1A1A',
          border: '1px solid rgba(255, 215, 0, 0.2)',
          borderRadius: '12px'
        }}>
          <Typography variant="body1" sx={{ color: '#FFFFFF', mb: 2 }}>
            {step.description}
          </Typography>
          <Typography variant="body2" sx={{ color: '#B3B3B3', mb: 2 }}>
            {step.details}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            bgcolor: '#0D0D0D',
            p: 2,
            borderRadius: '8px',
            border: '1px solid #2D2D2D'
          }}>
            <code style={{ flex: 1, color: '#FFD700', fontFamily: 'monospace' }}>
              {step.command}
            </code>
            <Tooltip title={t.copy}>
              <IconButton 
                size="small" 
                onClick={() => copyCommand(step.command)}
                sx={{ color: '#FFD700' }}
              >
                {copiedCommand === step.command ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Add navigation buttons for better UX */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            {index > 0 && (
              <Button
                size="small"
                onClick={() => setActiveStep(index - 1)}
                sx={{ color: '#FFD700' }}
              >
                {language === 'ar' ? 'السابق' : 'Previous'}
              </Button>
            )}
            {index < steps.length - 1 && (
              <Button
                size="small"
                variant="contained"
                onClick={() => setActiveStep(index + 1)}
                sx={{
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  '&:hover': { backgroundColor: '#FFE55C' }
                }}
              >
                {language === 'ar' ? 'التالي' : 'Next'}
              </Button>
            )}
            {index === steps.length - 1 && (
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setActiveStep(0);
                  // Optional: Scroll to top
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{
                  backgroundColor: '#4CAF50',
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                {language === 'ar' ? 'إكمال' : 'Complete'}
              </Button>
            )}
          </Box>
        </Paper>
      </StepContent>
    </Step>
  ))}
</Stepper>
        </Box>

        {/* Quick Commands */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 700,
              mb: 3,
              textAlign: 'center'
            }}
          >
            {t.quickCommands}
          </Typography>
          <Grid container spacing={2}>
            {commands.map((cmd, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  onClick={() => copyCommand(cmd.command)}
                  sx={{
                    bgcolor: '#1A1A1A',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#FFD700',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 20px rgba(255, 215, 0, 0.1)'
                    }
                  }}
                >
                  <CardContent>
                    <code style={{ color: '#FFD700', fontSize: '0.9rem', display: 'block', mb: 1 }}>
                      {cmd.command}
                    </code>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                      {cmd.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Troubleshooting */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#FFD700',
              fontWeight: 700,
              mb: 3,
              textAlign: 'center'
            }}
          >
            {t.troubleshooting}
          </Typography>
          <Grid container spacing={3}>
            {troubles.map((trouble, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{
                  bgcolor: '#1A1A1A',
                  border: '1px solid rgba(255, 215, 0, 0.2)',
                  borderRadius: '16px',
                  height: '100%',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#FFD700'
                  }
                }}>
                  <CardContent>
                    <Box sx={{ color: '#FFD700', fontSize: 32, mb: 2 }}>
                      {trouble.icon}
                    </Box>
                    <Typography variant="h6" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
                      {trouble.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B3B3B3' }}>
                      {trouble.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Support Section */}
        <Box sx={{
          bgcolor: '#1A1A1A',
          border: '1px solid rgba(255, 215, 0, 0.3)',
          borderRadius: '16px',
          p: { xs: 3, md: 5 },
          textAlign: 'center',
          mb: 4
        }}>
          <Typography variant="h5" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
            {t.needHelp}
          </Typography>
          <Typography variant="body1" sx={{ color: '#B3B3B3', mb: 3 }}>
            {t.supportText}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              startIcon={<EmailIcon />}
              href="mailto:support@qr-table.com"
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                '&:hover': {
                  borderColor: '#FFE55C',
                  bgcolor: 'rgba(255, 215, 0, 0.1)'
                }
              }}
              variant="outlined"
            >
              {t.supportEmail}
            </Button>
            <Button 
              startIcon={<DescriptionIcon />}
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                '&:hover': {
                  borderColor: '#FFE55C',
                  bgcolor: 'rgba(255, 215, 0, 0.1)'
                }
              }}
              variant="outlined"
            >
              {t.documentation}
            </Button>
            <Button 
              startIcon={<QuestionAnswerIcon />}
              sx={{
                color: '#FFD700',
                borderColor: '#FFD700',
                '&:hover': {
                  borderColor: '#FFE55C',
                  bgcolor: 'rgba(255, 215, 0, 0.1)'
                }
              }}
              variant="outlined"
            >
              {t.faq}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Divider sx={{ borderColor: 'rgba(255, 215, 0, 0.2)', my: 3 }} />
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" sx={{ color: '#6B6B6B' }}>
            {t.footer}
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B6B6B' }}>
            {t.lastUpdated}
          </Typography>
        </Box>
      </Container>

      {/* API Key Dialog */}
      <Dialog 
        open={apiKeyDialogOpen} 
        onClose={() => setApiKeyDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            border: '1px solid #FFD700',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKeyIcon />
            {t.yourApiKey}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {apiKeyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FFD700' }} />
            </Box>
          ) : apiKeyData ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{t.apiKey}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body1" sx={{ color: '#FFD700', fontFamily: 'monospace' }}>
                        {apiKeyData.apiKey}
                      </Typography>
                      <IconButton onClick={() => handleCopyToClipboard(apiKeyData.apiKey, 'key')} sx={{ color: '#FFD700' }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{t.apiSecret}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body1" sx={{ color: '#FF9800', fontFamily: 'monospace' }}>
                        {showApiSecret ? apiKeyData.apiSecret : '••••••••••••••••••••••••••••'}
                      </Typography>
                      <Box>
                        <IconButton onClick={() => setShowApiSecret(!showApiSecret)} sx={{ color: '#FFD700' }}>
                          {showApiSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        <IconButton onClick={() => handleCopyToClipboard(apiKeyData.apiSecret, 'secret')} sx={{ color: '#FFD700' }}>
                          <ContentCopyIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{t.status}</Typography>
                    <Box sx={{ mt: 1 }}>{getStatusChip(apiKeyData.status)}</Box>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{t.lastUsed}</Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', mt: 1 }}>
                      {apiKeyData.lastUsedAt ? new Date(apiKeyData.lastUsedAt).toLocaleString() : t.notUsedYet}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: '#1A1A1A', borderRadius: '12px' }}>
                    <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{t.expiresAt}</Typography>
                    <Typography variant="body2" sx={{ color: apiKeyData.expiresAt && new Date(apiKeyData.expiresAt) < new Date() ? '#F44336' : '#FFFFFF', mt: 1 }}>
                      {apiKeyData.expiresAt ? new Date(apiKeyData.expiresAt).toLocaleString() : t.neverExpires}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ color: '#B3B3B3', mb: 2 }}>
                {language === 'ar' ? 'لا يوجد مفتاح API للمطعم الخاص بك' : 'No API key found for your restaurant'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B6B6B' }}>
                {language === 'ar' 
                  ? 'يرجى الاتصال بالمسؤول لإنشاء مفتاح API للمطعم الخاص بك' 
                  : 'Please contact the administrator to create an API key for your restaurant'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #2D2D2D', p: 2 }}>
          <Button onClick={() => setApiKeyDialogOpen(false)} sx={{ color: '#B3B3B3' }}>
            {t.close}
          </Button>
          {apiKeyData && (
            <Button 
              onClick={() => {
                handleCopyToClipboard(apiKeyData.apiKey, 'key');
              }} 
              variant="contained"
              sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}
            >
              {t.copyKey}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{
            bgcolor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#FFD700' : '#FF3D00'}`,
            borderRadius: '12px',
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#FFD700' : '#FF3D00',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PrintServiceClient;